"use client";

import * as PopoverPrimitive from "@radix-ui/react-popover";
import clsx from "clsx";
import React from "react";
import FloatingPanel from "../../common/FloatingPanel/FloatingPanel";
import { DEFAULT_FLOATING_SIDE_OFFSET } from "../../common/FloatingPanel/positioning";
import styles from "./DateTimeInput.module.scss";

/**
 * A modal Radix dialog blocks wheel scrolling outside its content (react-remove-scroll), so a panel
 * portaled to document.body cannot be wheel-scrolled from inside a dialog. Portal into the dialog's
 * content element instead; outside a dialog this is undefined and Radix uses document.body.
 */
export const getPortalContainer = (element: Element | null | undefined) =>
  (element?.closest<HTMLElement>("[role='dialog']") ?? undefined) as HTMLElement | undefined;

export interface PickerPopoverProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Element the panel is positioned against. Pointer presses inside it do not dismiss the panel. */
  anchor: React.ReactElement;
  children: React.ReactNode;
  /**
   * When true the panel takes focus (and traps it) so keyboard users can work inside it. When
   * false, focus stays where it is (e.g. in a text input driving the panel) and mouse presses in
   * the panel do not steal it.
   */
  focusContent?: boolean;
  /** Called after the panel closes; return focus here (e.g. to the input that opened it). */
  onCloseFocus?: () => void;
  className?: string;
  contentProps?: Omit<React.ComponentPropsWithoutRef<typeof PopoverPrimitive.Content>, "children" | "asChild">;
}

export const PickerPopover = ({
  open,
  onOpenChange,
  anchor,
  children,
  focusContent = false,
  onCloseFocus,
  className,
  contentProps,
}: PickerPopoverProps) => {
  const anchorRef = React.useRef<HTMLDivElement | null>(null);

  return (
    <PopoverPrimitive.Root
      open={open}
      onOpenChange={onOpenChange}
      modal={focusContent}
    >
      <PopoverPrimitive.Anchor
        asChild
        ref={anchorRef}
      >
        {anchor}
      </PopoverPrimitive.Anchor>
      <PopoverPrimitive.Portal container={open ? getPortalContainer(anchorRef.current) : undefined}>
        <PopoverPrimitive.Content
          asChild
          align="start"
          sideOffset={DEFAULT_FLOATING_SIDE_OFFSET}
          collisionPadding={8}
          {...contentProps}
          onOpenAutoFocus={(event) => {
            // Without focusContent the input keeps focus; with it, the content picks its own target
            // (e.g. the calendar focuses the selected day).
            event.preventDefault();
            contentProps?.onOpenAutoFocus?.(event);
          }}
          onCloseAutoFocus={(event) => {
            event.preventDefault();
            onCloseFocus?.();
            contentProps?.onCloseAutoFocus?.(event);
          }}
          onInteractOutside={(event) => {
            if (!focusContent && anchorRef.current?.contains(event.target as Node)) event.preventDefault();
            contentProps?.onInteractOutside?.(event);
          }}
        >
          <FloatingPanel
            open
            className={clsx(styles.popoverContent, className)}
            onMouseDown={focusContent ? undefined : (event: React.MouseEvent) => event.preventDefault()}
          >
            {children}
          </FloatingPanel>
        </PopoverPrimitive.Content>
      </PopoverPrimitive.Portal>
    </PopoverPrimitive.Root>
  );
};
